'use client'
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { BalanceGrant } from '@/lib/billing/types'

import { grantSourceLabel } from '@/lib/billing/wallet/balance-details'
import { useLocale, useTranslations } from "next-intl"



/** Full date in the viewer's time zone: expiries can be a year away. */
export function formatExpiryDate(iso: string, timezone: string, locale?: string): string {
  return new Date(iso).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric', timeZone: timezone })
}

export function BalanceDetailsDialog({
  open,
  onOpenChange,
  grants,
  timezone,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  grants: BalanceGrant[]
  timezone: string
}) {
  const locale = useLocale()
  const { formatCredits } = useCreditFormatters()


  const tUi = useTranslations("Billing.BillingBalanceDetailsDialog")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle><span key={tUi("balanceDetails")}>{tUi("balanceDetails")}</span></DialogTitle>
          <DialogDescription><span key={tUi("creditsThatExpireFirstAreUsedFirst")}>{tUi("creditsThatExpireFirstAreUsedFirst")}</span></DialogDescription>
        </DialogHeader>
        <ul className="divide-y">
          {grants.map((grant, index) => (
            // Grants are listed once per open, in spending order, and never reordered.
            <li key={index} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2">
              <div className="min-w-0">
                <p className="break-words text-sm font-medium">{grantSourceLabel(grant.source, grant.planName)}</p>
                <p key={grant.expiresAt ? tUi("expiresValue0", { value0: String(formatExpiryDate(grant.expiresAt, timezone, locale)) }) : tUi("noExpiry")} className="text-xs text-muted-foreground">
                  {grant.expiresAt ? tUi("expiresValue0", { value0: String(formatExpiryDate(grant.expiresAt, timezone, locale)) }) : tUi("noExpiry")}
                </p>
              </div>
              <span className="font-mono text-sm">{formatCredits(grant.remainingMicrousd)}</span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  )
}
